import React, { useCallback, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import { T, useTranslate } from '@tolgee/react';
import { Sorting } from '../../../components/Sorting/Sorting';
import { SortingItemInterface } from '../../../components/Sorting/Sorting.interface';
import { cn } from '../../../lib/utils';
import {
	TransportConfig,
	TransportConfigRequest,
	TransportConfigService,
} from '../../../services/transport-config.services';
import FormInput from './components/FormInput';
import CategoryRow from './components/CategoryRow';
import ZoneHeader from './components/ZoneHeader';
import { toastVariant } from '../../../components/ToastVariant/toastVariant';

const SkeletonInput: React.FC<{ className?: string }> = ({
	className = '',
}) => (
	<div
		className={cn(
			'w-full h-[42px] animate-pulse bg-gray-10/50 rounded-[8px]',
			className,
		)}
	/>
);

const PLNValuesRow: React.FC<{
	rowIndex: number;
	formik: any;
	editFormula: boolean;
	loading?: boolean;
}> = ({ rowIndex, formik, editFormula, loading = false }) => {
	const { t } = useTranslate();

	return (
		<div className="flex gap-[14px] pl-[74px] pr-[33px]">
			{Array(6)
				.fill(null)
				.map((_, colIndex) =>
					loading ? (
						<SkeletonInput key={colIndex} className="w-full" />
					) : (
						<FormInput
							key={colIndex}
							name={`zones.plnValues[${rowIndex}][${colIndex}]`}
							placeholder={t(
								'companyInformationEditable.enterValue',
								'Enter value',
							)}
							className="w-full bg-[#EBECF2] rounded-[8px] transition hover:bg-white focus:bg-white"
							formik={formik}
							readOnly={!editFormula}
						/>
					),
				)}
		</div>
	);
};

const NUMBER_MAX_DECIMALS = /^(?:\d+|\d*\.\d{1,10})$/;

const TransportationFormula: React.FC = () => {
	const { t } = useTranslate();
	const [editFormula, setEditFormula] = useState<boolean>(false);
	const sortOptions = [
		{ title: t('adminProducts.poland', 'Poland'), value: 'PL' },
		{ title: t('adminProducts.moldova', 'Moldova'), value: 'MD' },
	];
	const [sorting, setSorting] = useState<SortingItemInterface>(sortOptions[0]);
	const [configData, setConfigData] = useState<TransportConfig | undefined>(
		undefined,
	);
	const [loading, setLoading] = useState<boolean>(false);
	const currency = sorting.value === 'PL' ? 'PLN' : 'MDL';

	const getTransportationConfig = useCallback(async (country: 'PL' | 'MD') => {
		setLoading(true);
		try {
			const { data } = await TransportConfigService.getTransportConfig(country);
			setConfigData(data);
		} catch (error) {
			console.error('Failed to fetch transportation config:', error);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		getTransportationConfig(sorting.value as 'PL' | 'MD').then(() => {});
	}, [getTransportationConfig, sorting.value]);

	const createInitialValues = (config: TransportConfig | undefined) => {
		if (!config) {
			return {
				margin: '10',
				categories: {
					category1: { from: '0', to: '100' },
					category2: { from: '100', to: '500' },
					category3: { from: '500', to: '1000' },
				},
				zones: {
					distances: Array(6)
						.fill(null)
						.map((_, i) => ({
							from: String(i * 50),
							to: String((i + 1) * 50),
						})),
					plnValues: Array(3)
						.fill(null)
						.map(() => Array(6).fill('0')),
				},
			};
		}

		return {
			margin: String(config.margin),

			categories: Object.fromEntries(
				Object.entries(config.categories).map(([key, { from, to }]) => [
					`category${key}`,
					{ from: String(from), to: String(to) },
				]),
			) as Record<
				'category1' | 'category2' | 'category3',
				{ from: string; to: string }
			>,

			zones: {
				distances: Object.entries(config.zones)
					.sort(([a], [b]) => Number(a) - Number(b))
					.map(([, { from, to }]) => ({
						from: String(from),
						to: to != null ? String(to) : '',
					})),

				plnValues: Object.entries(config.priceMatrix)
					.sort(([a], [b]) => Number(a) - Number(b))
					.map(([, { value }]) =>
						Object.entries(value)
							.sort(([a], [b]) => Number(a) - Number(b))
							.map(([, v]) => String(v)),
					),
			},
		};
	};

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: createInitialValues(configData),
		initialTouched: {
			margin: false,
		},
		onSubmit: async (values) => {
			try {
				const safeParseFloat = (value: string) => {
					const parsed = parseFloat(value);
					return isNaN(parsed) ? 0 : parsed;
				};

				const apiPayload: TransportConfigRequest = {
					countryCode: sorting.value as 'PL' | 'MD',
					margin: safeParseFloat(values.margin),
					categories: {
						'1': {
							from: safeParseFloat(values.categories.category1.from),
							to: safeParseFloat(values.categories.category1.to),
						},
						'2': {
							from: safeParseFloat(values.categories.category2.from),
							to: safeParseFloat(values.categories.category2.to),
						},
						'3': {
							from: safeParseFloat(values.categories.category3.from),
							to: safeParseFloat(values.categories.category3.to),
						},
					},
					zones: values.zones.distances.reduce<
						Record<string, { from: number; to?: number }>
					>((acc, { from, to }, idx) => {
						const key = String(idx);
						acc[key] = { from: safeParseFloat(from) };
						if (to !== '') acc[key].to = safeParseFloat(to);
						return acc;
					}, {}),
					priceMatrix: values.zones.plnValues.reduce<
						Record<string, { value: Record<string, number> }>
					>((acc, row, rowIdx) => {
						const catKey = String(rowIdx + 1);
						acc[catKey] = {
							value: row.reduce<Record<string, number>>(
								(vAcc, cell, colIdx) => {
									vAcc[String(colIdx)] = safeParseFloat(cell);
									return vAcc;
								},
								{},
							),
						};
						return acc;
					}, {}),
				};
				await TransportConfigService.updateTransportConfig(apiPayload);
				toastVariant(
					t(
						'transportationFormula.transportationFormulaUpdated',
						'Transportation Formula updated',
					),
					false,
				);

				setEditFormula(false);
			} catch (error) {
				console.error('Failed to update transport config:', error);
			}
		},
		validationSchema: Yup.object({
			margin: Yup.string()
				.required('Margin is required')
				.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number'),
			categories: Yup.object({
				category1: Yup.object({
					from: Yup.string()
						.required('From value is required')
						.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number'),
					to: Yup.string()
						.required('To value is required')
						.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number'),
				}),
				category2: Yup.object({
					from: Yup.string()
						.required('From value is required')
						.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number'),
					to: Yup.string()
						.required('To value is required')
						.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number'),
				}),
				category3: Yup.object({
					from: Yup.string()
						.required('From value is required')
						.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number'),
					to: Yup.string()
						.required('To value is required')
						.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number'),
				}),
			}).test(
				'category-validations',
				'Category validation',
				function (categories) {
					if (!categories) return true;

					const errors = [];
					const categoryList = ['category1', 'category2', 'category3'] as const;

					categoryList.forEach((catKey, index) => {
						const category = categories[catKey as keyof typeof categories];
						if (category) {
							const fromVal = parseFloat(category.from || '');
							const toVal = parseFloat(category.to || '');

							if (!isNaN(fromVal) && !isNaN(toVal) && fromVal > toVal) {
								errors.push(
									this.createError({
										path: `categories.${catKey}.from`,
										message: `Category ${index + 1} "from" must be ≤ "to"`,
									}),
								);
							}
						}
					});

					for (let i = 0; i < categoryList.length - 1; i++) {
						const currentCat =
							categories[categoryList[i] as keyof typeof categories];
						const nextCat =
							categories[categoryList[i + 1] as keyof typeof categories];

						if (currentCat && nextCat) {
							const toVal = parseFloat(currentCat.to || '');
							const nextFrom = parseFloat(nextCat.from || '');

							if (!isNaN(toVal) && !isNaN(nextFrom) && toVal > nextFrom) {
								errors.push(
									this.createError({
										path: `categories.${categoryList[i]}.to`,
										message: `Category ${i + 1} "to" must be ≤ Category ${
											i + 2
										} "from"`,
									}),
								);
							}
						}
					}

					if (errors.length > 0) {
						throw new Yup.ValidationError(errors);
					}

					return true;
				},
			),
			zones: Yup.object({
				distances: Yup.array()
					.of(
						Yup.object({
							from: Yup.string()
								.required('Distance from is required')
								.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number'),
							to: Yup.string()
								.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number')
								.notRequired(),
						}),
					)
					.test('zone-validations', 'Zone validation', function (distances) {
						if (!distances) return true;

						const errors = [];

						for (let i = 0; i < distances.length; i++) {
							const fromVal = parseFloat(distances[i].from || '');
							const toVal = parseFloat(distances[i].to || '');

							if (
								distances[i].to !== '' &&
								!isNaN(fromVal) &&
								!isNaN(toVal) &&
								fromVal > toVal
							) {
								errors.push(
									this.createError({
										path: `zones.distances[${i}].from`,
										message: `Zone ${i} "from" must be ≤ "to"`,
									}),
								);
							}
						}

						for (let i = 0; i < distances.length - 1; i++) {
							const toVal = parseFloat(distances[i].to || '');
							const nextFrom = parseFloat(distances[i + 1].from || '');

							if (!isNaN(toVal) && !isNaN(nextFrom) && toVal > nextFrom) {
								errors.push(
									this.createError({
										path: `zones.distances[${i}].to`,
										message: `Zone ${i} "to" must be ≤ Zone ${i + 1} "from"`,
									}),
								);
							}
						}

						if (errors.length > 0) {
							throw new Yup.ValidationError(errors);
						}

						return true;
					}),
				plnValues: Yup.array().of(
					Yup.array().of(
						Yup.string()
							.required(`${currency} value is required`)
							.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number'),
					),
				),
			}),
		}),
	});

	const hasMarginError = !!(formik.touched.margin && formik.errors.margin);
	const hasZoneRowFromError = formik.values.zones.distances.some(
		(_, i) =>
			formik.touched.zones?.distances?.[i]?.from &&
			// @ts-ignore
			formik.errors.zones?.distances?.[i]?.from,
	);
	const hasZoneRowToError = formik.values.zones.distances.some(
		(_, i) =>
			formik.touched.zones?.distances?.[i]?.to &&
			// @ts-ignore
			formik.errors.zones?.distances?.[i]?.to,
	);

	const errorOffset = (() => {
		const hasMarginError = !!(formik.touched.margin && formik.errors.margin);
		const hasZoneFromError = formik.values.zones.distances.some(
			(_, i) =>
				formik.touched.zones?.distances?.[i]?.from &&
				// @ts-ignore
				formik.errors.zones?.distances?.[i]?.from,
		);
		const hasZoneToError = formik.values.zones.distances.some(
			(_, i) =>
				formik.touched.zones?.distances?.[i]?.to &&
				// @ts-ignore
				formik.errors.zones?.distances?.[i]?.to,
		);

		if (hasZoneFromError && hasZoneToError) return 40;
		if (hasMarginError || hasZoneFromError || hasZoneToError) return 20;
		return 0;
	})();

	return (
		<form onSubmit={formik.handleSubmit}>
			<main className="relative flex flex-col gap-5 w-[calc(100vw-67px)] xl-2xl:w-[calc(100vw-83px)] h-full">
				<header className="hidden xl:flex flex-col gap-4 mx-7.5">
					<div className="h-[44px] flex items-center gap-[24px]">
						<SypacText variant="heading-4">
							<p className="text-gray-80">
								<T keyName="transportationFormula.costFormulaParameters">
									Transportation formula cost formula parameters
								</T>
							</p>
						</SypacText>
						<div className="w-[1px] h-[12px] bg-gray-10" />
						<Sorting
							options={sortOptions}
							action={(item) => setSorting(item)}
							current={sorting}
							label={{ title: 'Country:', key: 'country' }}
							includeDefault={false}
						/>

						<div className="ml-auto">
							{!editFormula ? (
								<SypacButton variant="subTitle">
									<button
										type="button"
										className="w-fit p-0 m-0 flex justify-center items-center border-0"
										onClick={() => setEditFormula(true)}
									>
										<SypacText variant="body-regular-medium">
											<p className="text-cornflower-blue underline underline-offset-[3.5px] transition hover:text-cornflower-blue/80 hover:decoration-cornflower-blue/60">
												<T keyName="transportationFormula.editFormula">
													Edit transportation formula
												</T>
											</p>
										</SypacText>
									</button>
								</SypacButton>
							) : (
								<div className="w-full flex justify-center gap-4">
									<SypacButton variant="secondary" size="small">
										<button
											type="button"
											className="w-[248px] h-[44px] rounded-[8px]"
											onClick={() => {
												formik.resetForm();
												setEditFormula(false);
											}}
										>
											<SypacText variant="body-regular-medium">
												<p>
													<T keyName="transportationFormula.closeEdit">
														Close edit
													</T>
												</p>
											</SypacText>
										</button>
									</SypacButton>
									<SypacButton variant="primary" size="small">
										<button
											className="w-[248px] h-[44px] rounded-[8px]"
											type="submit"
											disabled={!formik.isValid || formik.isSubmitting}
										>
											<SypacText
												variant="body-regular-medium"
												className="flex items-center gap-4"
											>
												<p>
													<T keyName="transportationFormula.saveChanges">
														Save changes
													</T>
												</p>
											</SypacText>
										</button>
									</SypacButton>
								</div>
							)}
						</div>
					</div>

					<SypacText variant="body-regular-medium">
						<p className="text-gray-40">
							<T keyName="transportationFormula.zoneDistance">
								Zone distance (Loading to unloading place)
							</T>
						</p>
					</SypacText>
				</header>
				<div className="border-0 border-solid border-b-[1px] border-gray-10" />

				<section className="flex items-center gap-3 xl:gap-[40px] ml-5 mr-7.5 xl:mx-7.5">
					<div className="relative w-full h-fit flex flex-col items-center justify-center border border-solid border-gray-10 rounded-[10px] overflow-hidden">
						<div className="w-full h-[44px] bg-gray-1 self-start border border-solid border-t-0 -mx-[1px] border-gray-10 rounded-[6px]" />

						<div className="w-full flex gap-[29px] -mt-[45px] mb-[10px]">
							<div className="w-[486px] h-fit pl-[12px] flex flex-col items-center justify-center">
								<p className="h-[24px] m-0 mt-[10px]">{sorting?.value}</p>

								<div
									className="w-full grid grid-cols-[116px,auto] mt-[49px]"
									style={{
										marginBottom: hasMarginError
											? hasZoneRowFromError && hasZoneRowToError
												? '20px'
												: undefined
											: hasZoneRowFromError && hasZoneRowToError
											? '40px'
											: hasZoneRowFromError || hasZoneRowToError
											? '20px'
											: undefined,
									}}
								>
									<SypacText variant="body-regular-medium">
										<p className="text-gray-80 mt-[10px]">
											<T keyName="transportationFormula.margin">Margin</T>
										</p>
									</SypacText>
									{loading ? (
										<SkeletonInput className="bg-gray-10/50" />
									) : (
										<FormInput
											name="margin"
											className="bg-[#EBECF2] rounded-[8px] transition hover:bg-white focus:bg-white"
											placeholder={t(
												'companyInformationEditable.enterMarginRate',
												'Enter margin rate',
											)}
											readOnly={!editFormula}
											formik={formik}
										/>
									)}
								</div>

								<div className="w-fit flex gap-[161px] items-center -ml-[50px] mt-[52px]">
									<SypacText variant="body-regular-medium">
										<p className="text-gray-80">
											<T keyName="transportationFormula.from">from</T>
										</p>
									</SypacText>
									<SypacText variant="body-regular-medium">
										<p className="text-gray-80">
											<T keyName="transportationFormula.to">to</T>
										</p>
									</SypacText>
								</div>

								<div className="w-full flex flex-col gap-3 mt-[25px]">
									{['category1', 'category2', 'category3'].map((cat, idx) =>
										loading ? (
											<div
												key={cat}
												className="w-full grid grid-cols-[116px,auto] gap-3"
											>
												<SypacText variant="body-regular-medium">
													<p className="text-gray-80 mt-[10px]">
														Category {idx + 1}
													</p>
												</SypacText>
												<div className="flex gap-[14px] -ml-3">
													<SkeletonInput />
													<SkeletonInput />
												</div>
											</div>
										) : (
											<CategoryRow
												key={cat}
												index={idx}
												name={cat}
												formik={formik}
												editFormula={editFormula}
											/>
										),
									)}
								</div>

								<div className="w-fit flex items-center -ml-[224px] mt-3">
									<SypacText variant="body-regular-medium">
										<p className="text-gray-80">
											<T keyName="transportationFormula.tons">tons</T>
										</p>
									</SypacText>
								</div>
							</div>

							<div className="w-[calc(100%-528px)] h-fit pr-[12px] flex flex-col items-center justify-center">
								<div className="w-full flex gap-3 mt-[10px]">
									<div className="w-full flex flex-col gap-3">
										<div className="w-full grid grid-cols-[74px,auto]">
											<SypacText variant="body-regular-medium">
												<p className="text-gray-80 mt-[56px]">
													<T keyName="transportationFormula.fromFrom">From</T>
												</p>
											</SypacText>
											<span
												className={cn(
													'flex gap-[14px]',
													loading && 'mt-[44px]',
												)}
											>
												{Array(6)
													.fill(null)
													.map((_, idx) => {
														if (loading) {
															return (
																<SkeletonInput key={idx} className="w-full" />
															);
														}
														const thisError =
															formik.touched.zones?.distances?.[idx]?.from &&
															// @ts-ignore
															formik.errors.zones?.distances?.[idx]?.from;
														return (
															<ZoneHeader
																key={idx}
																index={idx}
																formik={formik}
																className={
																	hasZoneRowFromError && !thisError
																		? 'mb-[20px]'
																		: ''
																}
																editFormula={editFormula}
															/>
														);
													})}
											</span>
										</div>
										<div
											className="w-full grid grid-cols-[74px,auto]"
											style={{
												marginBottom:
													hasMarginError &&
													!hasZoneRowFromError &&
													!hasZoneRowToError
														? '20px'
														: undefined,
											}}
										>
											<SypacText variant="body-regular-medium">
												<p className="text-gray-80 mt-[10px]">
													<T keyName="transportationFormula.toTo">To</T>
												</p>
											</SypacText>
											<span className="flex gap-[14px]">
												{Array(6)
													.fill(null)
													.map((_, idx) =>
														loading ? (
															<SkeletonInput key={idx} className="w-full" />
														) : (
															<FormInput
																key={idx}
																name={`zones.distances[${idx}].to`}
																placeholder={
																	idx === 5
																		? '+'
																		: t(
																				'companyInformationEditable.enterValue',
																				'Enter value',
																		  )
																}
																readOnly={!editFormula}
																className={cn(
																	'w-full bg-[#FAFAFA] rounded-[8px] transition hover:bg-white focus:bg-white',
																	idx === 5 && 'placeholder:text-gray-80',
																)}
																formik={formik}
															/>
														),
													)}
											</span>
										</div>
									</div>
									<SypacText variant="body-regular-medium">
										<p className="text-gray-80 mt-[83px]">
											<T keyName="transportationFormula.km">km</T>
										</p>
									</SypacText>
								</div>

								<div className="w-full flex gap-[39px] items-center mt-[25px]">
									<SypacText variant="body-regular-medium">
										<p className="w-[35px] text-gray-80">{currency}</p>
									</SypacText>
									<SypacText
										variant="body-regular-medium"
										className="w-full pr-[33px]"
									>
										<p className="w-full text-gray-80 text-center">
											{currency} per km
										</p>
									</SypacText>
								</div>

								<div className="w-full flex flex-col gap-3 mt-[25px]">
									{Array(3)
										.fill(null)
										.map((_, idx) => (
											<PLNValuesRow
												key={idx}
												rowIndex={idx}
												formik={formik}
												editFormula={editFormula}
												loading={loading}
											/>
										))}
								</div>
							</div>
						</div>

						<div className="absolute top-0 left-[512px] w-[1px] h-full bg-gray-10" />
						<div
							className="absolute w-full h-[1px] bg-gray-10"
							style={{ top: `${164 + errorOffset}px` }}
						/>
						<div
							className="absolute w-full h-[1px] bg-gray-10"
							style={{ top: `${211 + errorOffset}px` }}
						/>
					</div>
				</section>
			</main>
		</form>
	);
};

export default TransportationFormula;
